import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { FileTypeEnum } from '@repo/toolkit/types'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import DataTable, { Column, PaginationInput } from '@repo/ui/data-table'
import { DocumentIcon, DownloadFile, PdfIcon, PlusIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'

import { createTripFolderDocument } from '../../../api/trip-folder/create-trip-folder-document'
import { getTripFolderDocuments } from '../../../api/trip-folder/get-trip-folder-documents'
import CreateTripFolderDocumentForm from '../../../components/pages/trip-folders/create-trip-folder-document-form'
import DeleteTripFolderDocumentButton from '../../../components/pages/trip-folders/delete-trip-folder-document-button'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { FileType } from '../../../lib/types/models/file/file'
import { TripFolderDocumentInput } from '../../../lib/types/models/trip-folder/trip-folder-input'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { queryClient } from '../../../main'

interface FolderDocumentsProps {
  folderId: number
}

const FolderDocuments = ({ folderId }: FolderDocumentsProps) => {
  const [tableState, setTableState] = useState<Partial<PaginationInput>>({
    sorting: []
  })

  const { modalState, closeModel, toggleModal } = useModalManager<
    'create-trip-folder-document' | 'delete-document',
    TripFolderDocumentInput | FileType
  >(['create-trip-folder-document', 'delete-document'])

  const { data, isLoading } = useQuery({
    queryKey: ['trip-folder-documents', folderId],
    queryFn: () => getTripFolderDocuments(folderId)
  })

  const { mutateAsync: createDocument } = useMutation({
    mutationFn: createTripFolderDocument,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trip-folder-documents', folderId] })
      toast.success('Document créé avec succès')
      closeModel('create-trip-folder-document')
    },
    onError: showAxiosError
  })

  const handleCreateDocument = async (input: TripFolderDocumentInput) => {
    await createDocument({ id: folderId, ...input })
  }

  const columns = useMemo<Column<FileType>[]>(
    () => [
      {
        header: 'Nom du document',
        accessorKey: 'name',
        enableSorting: true,
        sortingFn: (rowA, rowB) => rowA.original.name.localeCompare(rowB.original.name),
        cell: ({ row }) => (
          <Typography variant="secondary-body">{valueRender(row.original.name)}</Typography>
        )
      },
      {
        header: 'Téléchargé le',
        accessorKey: 'createdAt',
        enableSorting: true,
        cell: ({ row }) => (
          <Badge text={DateFormatter.DDMMYYYY(row.original.createdAt)} variant="grey" />
        )
      },
      {
        header: 'Document',
        accessorKey: 'path',
        cell: ({ row }) => (
          <Button
            variant="ghost"
            icon={row.original.type === FileTypeEnum.IMAGE ? DocumentIcon : PdfIcon}
            className="w-fit p-0 px-2"
            onClick={() => {
              window.open(row.original.path, '_blank')
            }}
          />
        )
      },
      {
        header: '',
        accessorKey: 'actions',
        cell: ({ row }) => {
          return <DeleteTripFolderDocumentButton folderId={folderId} document={row.original} />
        }
      }
    ],
    [toggleModal]
  )

  return (
    <div className="py-3 flex flex-col gap-4">
      <Typography variant="sub-heading">Documents annexes</Typography>

      <Button
        variant="primary"
        icon={PlusIcon}
        label="Ajouter un document"
        className="w-fit"
        onClick={() => toggleModal('create-trip-folder-document')}
      />

      <DataTable
        disableHover
        isLoading={isLoading}
        columns={columns}
        data={data ?? []}
        rowCount={data?.length ?? 0}
        paginated={false}
        state={tableState}
        onStateChange={setTableState}
        manualSorting={false}
      />

      <Modal
        hideActions
        hasBorderBottom
        size="small"
        icon={DownloadFile}
        title={'Téléverser un nouveau document'}
        isOpen={modalState['create-trip-folder-document']}
        onCancel={() => closeModel('create-trip-folder-document')}
      >
        <CreateTripFolderDocumentForm
          className={MODAL_BODY_CLASSNAME}
          buttonsConfig={modalButtonConfig({
            onCancel: () => toggleModal('create-trip-folder-document'),
            onConfirm: handleCreateDocument
          })}
        />
      </Modal>
    </div>
  )
}

export default FolderDocuments
